import React, { ClipboardEvent } from "react"
import {
  Flex, IconButton, Input, InputGroup, InputLeftElement, InputRightElement,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import CopyIconButton from "components/elements/CopyIconButton"
import CopyToClipboard from "components/elements/CopyToClipboard"
import { removeProtocolFromUrl } from "utilities/strings"

type Props = {
  url: string
  withOpen?: boolean
}

const copyFullUrl = (url: string) => (event: ClipboardEvent<HTMLInputElement>) => {
  const { selectionStart, selectionEnd, value } = event.currentTarget

  if (selectionStart === 0 && selectionEnd === value.length) {
    event.clipboardData.setData("text/plain", url)
    event.preventDefault()
  }
}

const LinkField = ({ url, withOpen }: Props) => {
  const value = withOpen ? removeProtocolFromUrl(url) : url
  const compact = withOpen
    ? {
      size: "sm", flex: 1, flexBasis: `calc(${value.length}ch + 3rem)`, "data-testid": "link-field-group",
    }
    : {}
  const field = (
    <InputGroup {...compact}>
      <InputLeftElement pointerEvents="none" color="gray.500">
        <Icon icon="link-chain" size={4} />
      </InputLeftElement>
      <Input
        value={value}
        borderRadius={withOpen ? "md" : undefined}
        isReadOnly
        bg="gray.50"
        borderColor="gray.300"
        fontWeight="semibold"
        pr={withOpen ? undefined : 10}
        aria-label="Link"
        onCopy={withOpen ? copyFullUrl(url) : undefined}
      />
      {!withOpen && (
        <InputRightElement>
          <CopyIconButton text={url} />
        </InputRightElement>
      )}
    </InputGroup>
  )

  if (!withOpen) {
    return field
  }

  return (
    <Flex gap={1.5} flexWrap="wrap" data-testid="link-field">
      {field}
      <CopyToClipboard
        text={url}
        size="sm"
        variant="outline"
        colorScheme="gray"
        leftIcon="copy"
        iconSpacing={1.5}
        px={2.5}
        minW="5rem"
        copiedContent="Copied"
        flexShrink={0}
      >
        Copy
      </CopyToClipboard>
      <IconButton
        as="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        size="sm"
        variant="outline"
        bg="white"
        flexShrink={0}
        aria-label="Open event link"
        icon={<Icon icon="link-external" size={3} />}
      />
    </Flex>
  )
}

export default LinkField
